import { useEffect } from 'react';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
// material
import { Box, Card, Container, Stack, Typography, Button, Link, styled, Fade } from '@mui/material';
// router
import { useNavigate, useParams } from 'react-router-dom';
// GRAPHQL
import { useQuery, useMutation } from '@apollo/client';
import { AdminGQLFunction } from '../graphql/ADMIN';

// hooks
import useResponsive from '../hooks/useResponsive';
// components
import Page from '../components/Page';
import BasicBreadcrumb from '../components/BasicBreadcrumb';
import LoadingInfo from '../sections/people/LoadingInfo';
import KeyValueDisplay from '../components/KeyValueDisplay';
import KeyValueLabelDisplay from '../components/KeyValueLabelDisplay';

// info
import { ADMIN } from '../info/ADMIN';

const RootStyle = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center'
}));
const ContentStyle = styled(Card)(({ theme }) => ({
  width: '100%',
  // maxWidth: '50%',
  margin: theme.spacing(2),
  display: 'flex',
  justifyContent: 'flex-end',
  flexDirection: 'column',
  padding: theme.spacing(2)
}));

function DataDisplay({ data, smUp, t }) {
  return (
    <Box
      sx={{
        display: 'grid',
        columnGap: 2,
        rowGap: 3,
        gridTemplateColumns: smUp ? 'repeat(2, 1fr)' : '1fr',
        m: 1,
        mb: 4
      }}
    >
      <KeyValueDisplay title={t('common.id')} data={data.adminInfo.id} />
      <KeyValueDisplay title={t('common.username')} data={data.adminInfo.username} />
      <KeyValueDisplay title={t('common.email')} data={data.adminInfo.email} />
      <KeyValueDisplay title={t('common.telephone')} data={data.adminInfo.telephone || '-'} />
      <KeyValueDisplay title={t('login.password')} data={data.adminInfo.password
           ? '*'.repeat(data.adminInfo.password.length)
           : '-'} />
      <KeyValueLabelDisplay
        title={t('people.account_state')}
        data={t(`common.${data.adminInfo.userState ? 'blocked' : 'normal'}`)}
        color={!data.adminInfo.userState ? 'success' : 'warning'}
      />
    </Box>
  );
}

export default function AdminInfo() {
  const { t } = useTranslation();
  const smUp = useResponsive('up', 'sm');
  const { adminId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const { loading: queryLoading, data: queryData } = useQuery(AdminGQLFunction.GET_ADMIN_INFO, {
    variables: { id: adminId },
    fetchPolicy: 'network-only'
  });

  const [deleteAdmin] = useMutation(AdminGQLFunction.DELETE_ADMIN, {
    variables: { id: adminId },
    onCompleted: () => {
      enqueueSnackbar(t('common.success'), { variant: 'success' });
      navigate('/admin', { replace: true });
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  });

  const bread = [
    <Link href="/admin" underline="hover" key="1" color="inherit">
      {t('admin.title')}
    </Link>,
    <Typography key="2" color="text.primary">
      {t('people.info_title', { identity: t('nav.admin') })}
    </Typography>
  ];

  const identityName = t('nav.admin');

  return (
    <Page title={t('people.info_title', { identity: identityName })}>
      <Container maxWidth={false}>
        <Stack direction="column">
          <Typography variant="h4" gutterBottom>
            {t('people.info_title', { identity: identityName })}
          </Typography>
          <BasicBreadcrumb bread={bread} />
        </Stack>

        <RootStyle>
          <ContentStyle>
            {!queryLoading && queryData ? <DataDisplay data={queryData} smUp={smUp} t={t} /> : <LoadingInfo smUp={smUp} />}

            <Stack direction={smUp ? 'row' : 'column'} justifyContent="space-between" alignItems="center" spacing={2}>
              <Button
                sx={{ maxWidth: smUp ? '200px' : '100%' }}
                fullWidth
                variant="contained"
                onClick={() => {
                  navigate(`/admin/info/edit/${adminId}`);
                }}
              >
                {t('common.edit')}
              </Button>
              <Button
                sx={{ maxWidth: smUp ? '200px' : '100%' }}
                fullWidth
                variant="outlined"
                color="error"
                onClick={() => {
                  confirm({
                    title: t('people.delete_confirm_title', { identity: identityName }),
                    description: t('people.delete_confirm_desc', { identity: identityName }),
                    confirmationText: t('common.delete'),
                    cancellationText: t('common.cancel'),
                    confirmationButtonProps: { color: 'error' }
                  })
                    .then(() => {
                      deleteAdmin();
                    })
                    .catch(() => {
                      enqueueSnackbar(t('people.delete_cancel', { identity: identityName }), {
                        TransitionComponent: Fade,
                        preventDuplicate: true,
                        variant: 'warning'
                      });
                    });
                }}
              >
                {t('common.delete')}
              </Button>
            </Stack>
          </ContentStyle>
        </RootStyle>
      </Container>
    </Page>
  );
}
